import React, { useCallback } from 'react';
import './TemplatePlantConfigurator.scss';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';
import { useStores } from '../../hooks/use-stores';
import Button from 'react-bootstrap/lib/Button';
import cx from 'classnames';

/**
 * Allows the user to pre-configure the plant using a .json template.
 * Should show only when plant has no devices configured.
 */
export const TemplatePlantConfigurator = () => {
  const { t } = useTranslation();
  const { newAuthStore, locationsStore } = useStores();
  const { plantId } = locationsStore;
  const [error, setError] = React.useState<string | null>(null);
  const [uploadInProgress, setUploadInProgress] = React.useState<boolean>(false);
  const [includeMaterials, setIncludeMaterials] = React.useState<boolean>(true);

  const uploadConfiguration = useCallback(async (form: HTMLFormElement) => {
    try {
      const formData = new FormData(form);
      const url = `/api/plant/${plantId}/configuration/template?includeMaterials=${includeMaterials}`;
      const response = await newAuthStore.postFormData(url, formData);

      const data = await response.json();
      if (!response.ok) {
        setError(JSON.stringify(data.errors));
        return;
      }

      window.location.reload();
    } catch (error) {
      logger.error('Error configuring plant', error);
      setError(error instanceof Error ? error.message : 'Error configuring plant. Please try again.');
    } finally {
      setUploadInProgress(false);
    }
  }, [newAuthStore, plantId, includeMaterials]);

  const handleFormSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const file = new FormData(event.currentTarget).get('file');
    if (!file) {
      logger.error('No file selected');
      setError(t('No file selected'));
      return;
    }

    setUploadInProgress(true);
    await uploadConfiguration(event.currentTarget);
  }, [t, uploadConfiguration]);

  if (!plantId) {
    return (
      <div className="plant-configurator-container">
        <p>{t('Loading plant Id...')}</p>
      </div>
    );
  }

  return (
    <div
      className={cx(
        'plant-configurator-container',
      )}
    >
      <form
        className={cx(
          'plant-configurator',
          error && 'plant-configurator--error',
        )}
        onSubmit={handleFormSubmit}
      >
        <h3>{t('Create a plant using a template file')}</h3>
        <label htmlFor="template-file">{t('Upload a template file')}</label>
        <input
          type="file"
          id="template-file"
          name="file"
          accept=".json"
          required
        />
        <label style={{ display: 'flex', alignItems: 'center', marginTop: '10px', marginBottom: '10px' }}>
          <input
            type="checkbox"
            checked={includeMaterials}
            onChange={(e) => setIncludeMaterials(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          {t('Include materials')}
        </label>
        <Button
          type="submit"
          disabled={uploadInProgress}
          className={'btn btn-primary'}
        >
          {uploadInProgress ? t('Uploading...'):t('Configure plant')}
        </Button>
        {error && (
          <p className="plant-configurator__error">
            {error}
          </p>
        )}
      </form>
    </div>
  );
};
